import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { DetailsConstructionItem } from '@/app/models/detailsConstruction';
import { useUpdateDetailsConstructionMutation } from '@/app/services/detailsConstructionApi';
import { useSearchContractTypeQuery } from '@/app/services/financialClassificationApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import formatterMonetaryValue, {
  cpfOrCpnjFromBack,
  formatCurrency,
  formatHandleCPFAndCNPJ,
  isSupplierCodeValid,
} from '@/app/utils/numberUtils';
import FormFieldDate from '@/components/common/DateField';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import useFormattedCpfAndCpnj from '@/hooks/useFormattedCpfAndCpnj';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import FormField from '../baseForms/FormField';
import FormFieldLong from '../baseForms/FormFieldLong';
import FormFieldSelect from '../baseForms/FormFieldSelect';
import LaunchDetailsBox from '../baseForms/info/LaunchDetailsBox';

interface NewEditConstructionDetailsProps {
  selectItem: DetailsConstructionItem;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditConstructionDetails(props: NewEditConstructionDetailsProps) {
  const theme = useTheme();
  const { t } = useTranslation(['construction', 'common']);

  const { selectItem, setDrawerOpen } = props;

  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectItem?.value);
  const { value: supplierCode, setValue: setSupplierCode } = useFormattedCpfAndCpnj(selectItem?.supplierCode);

  const [justification, setJustification] = useState<string>(selectItem.justify ? selectItem.justify : '');
  const [launchComplement] = useState<string>(selectItem.complement ? selectItem.complement : '');

  const [description, setDescription] = useState<string>(
    selectItem?.description != null ? selectItem?.description : '',
  );

  const [supplier, setSupplier] = useState<string>(selectItem?.supplier != null ? selectItem?.supplier : '');

  const initialDate = selectItem?.initialDate ? dayjs(selectItem.initialDate, 'YYYY-MM-DD') : null;
  const finalDate = selectItem?.finalDate ? dayjs(selectItem.finalDate, 'YYYY-MM-DD') : null;

  const [contractType, setContractType] = useState<string>(
    selectItem.contractType != null ? selectItem?.contractType.id : '',
  );
  const { data: financialContractType } = useSearchContractTypeQuery();

  const [expenseDate, setExpenseDate] = useState<string>(
    selectItem?.expenseDate ? dayjs(selectItem?.expenseDate).format('DD/MM/YYYY') : '',
  );

  const [update] = useUpdateDetailsConstructionMutation();

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        id: selectItem?.id as number,
        description,
        value: parseFloat(totalValue.replace('R$', '').replace('.', '').replace(',', '.')) || DEFAULTVALUE,
        supplier,
        supplierCode: cpfOrCpnjFromBack(supplierCode),
        justify: justification,
        initialDate: initialDate ? initialDate.format('YYYY-MM-DD') : '',
        finalDate: finalDate ? finalDate.format('YYYY-MM-DD') : '',
        contractType: {
          id: Number(contractType),
        },
        expenseDate: formatDateForBackend(expenseDate as string),
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('edit.details.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setTotalValue(formatCurrency(raw));
  };

  const handleSupplierCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatHandleCPFAndCNPJ(e.target.value);
    setSupplierCode(formatted);
  };
  const supplierCodeIsValid = isSupplierCodeValid(supplierCode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('common:edit.detail')} />
        <LaunchDetailsBox
          totalValue={formatterMonetaryValue(selectItem?.value || null)}
          launchComplement={launchComplement}
          complementDisabled
        />

        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />

        <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
          <FormField
            label={t('common:supplier') as string}
            value={supplier}
            onChange={(e) => {
              setSupplier(e.target.value);
            }}
          />

          <FormField
            label={t('common:cnpj') as string}
            value={supplierCode}
            onChange={handleSupplierCode}
            error={!supplierCodeIsValid}
          />

          <FormField label={t('common:total.value') as string} value={totalValue} onChange={handleValueChange} />

          <FormField
            label={t('construction') as string}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
          <FormFieldDate label={t('common:expenseDate')} value={expenseDate} onChange={setExpenseDate} />

          <FormFieldSelect
            label={t('contract.type')}
            value={contractType}
            onChange={setContractType}
            options={financialContractType ?? []}
            placeholder={t('select.contract.type') as string}
          />

          <FormFieldLong
            label={t('common:justification')}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={!supplierCodeIsValid || !expenseDate} />
      </Box>
    </Box>
  );
}
